import { API_CONFIG } from "../config/environment";

const createAuthHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  if (response.status === 401 || response.status === 403) {
    console.warn("🔐 Token expirado o no autorizado en API");
    window.dispatchEvent(
      new CustomEvent("token-expired", {
        detail: { status: response.status },
      }),
    );

    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || "Token expirado o no autorizado",
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || `HTTP error! status: ${response.status}`,
    );
  }

  return response.json();
};

const BASE = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/price-details`;

export const priceDetailsService = {
  /**
   * Obtener los detalles de precio (inputs guardados) de un lote.
   * Devuelve null si el lote aún no tiene detalles de precio (404).
   *
   * @param {number|string} lotId
   * @param {string} token
   * @returns {Promise<Object|null>}
   */
  getPriceDetails: async (lotId, token) => {
    if (!token) {
      throw new Error("Token requerido para acceder a detalles de precio");
    }

    const url = `${BASE}/${lotId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      // 404 = el lote todavía no tiene detalles de precio (modo creación)
      if (response.status === 404) {
        return null;
      }

      const data = await handleResponse(response);
      return data?.data ?? null;
    } catch (error) {
      console.error(`❌ Error fetching price details for lot ${lotId}:`, error);
      throw error;
    }
  },

  /**
   * Crear o actualizar los detalles de precio de un lote.
   * Solo se envían los campos de ENTRADA; los derivados (netPrice, totalPrice,
   * transferTax, etc.) los recalcula el backend.
   *
   * Si el lote ya tenía detalles, el backend puede encolar un PendingChange
   * (flujo 4-eyes); en ese caso la respuesta incluye `data.pendingChange`.
   *
   * @param {number|string} lotId
   * @param {Object} priceData - Inputs del formulario
   * @param {string} token
   * @returns {Promise<Object>}
   */
  upsertPriceDetails: async (lotId, priceData, token) => {
    if (!token) {
      throw new Error("Token requerido para guardar detalles de precio");
    }

    const url = `${BASE}/${lotId}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: createAuthHeaders(token),
        body: JSON.stringify(priceData),
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error(`❌ Error saving price details for lot ${lotId}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar los detalles de precio de un lote.
   *
   * @param {number|string} lotId
   * @param {string} token
   * @returns {Promise<Object>}
   */
  deletePriceDetails: async (lotId, token) => {
    if (!token) {
      throw new Error("Token requerido para eliminar detalles de precio");
    }

    const url = `${BASE}/${lotId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: createAuthHeaders(token),
      });

      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error(`❌ Error deleting price details for lot ${lotId}:`, error);
      throw error;
    }
  },
};

export default priceDetailsService;
